import Papa from 'papaparse';
import { saveAs } from 'file-saver';
import chroma from 'chroma-js';

export * from './enums';
export * from './measure';
export * from './table';
export * from './transform';
export * from './formatters';
export * from './customData';
export * from './queryStringBuilder';

const DISTRIBUTED_COLORS = [
  '#003e96',
  '#ff8a5d',
  '#8c4edd',
  '#d58100',
  '#ffa400',
  '#ecdb53',
  '#c9b400',
  '#d05234',
  '#5a8ed9',
  '#a49aff',
];

const distributedColorsLength = DISTRIBUTED_COLORS.length;

export default {
  getWindowResolution() {
    const screenWidth = window.screen.width * window.devicePixelRatio;
    const screenHeight = window.screen.height * window.devicePixelRatio;
    const resolutionRatio = screenWidth / screenHeight;
    // Condition applies when ratio > 16:10 (1.6)
    return resolutionRatio > 1.6
      ? {
          width: window.screen.width * window.devicePixelRatio,
          height: window.screen.height,
        }
      : {
          width: window.screen.width * window.devicePixelRatio,
          height: ((window.screen.height + 50) * (resolutionRatio / 1.6)).toFixed(0),
        };
  },
  round(value, decimals) {
    return Number(`${Math.round(`${value}e${decimals}`)}e-${decimals}`);
  },
  percentageFormatter(params) {
    return `${this.round(parseFloat(params.value) * 100, 2)}%`;
  },
  formatImageNameForDisplay(param) {
    const imageNameStringBlackList = ['.col-pasdanorama_sh', '.col-panorama_sh'];
    let substring = '';
    if (param) {
      substring = param.substr(0, param.lastIndexOf('.')) || param;
      imageNameStringBlackList.forEach((iterator) => {
        if (substring.indexOf(iterator) !== -1) {
          substring = substring.replace(iterator, '');
        }
      });
    }
    return substring;
  },
  equipmentCsvDataCleaner(data) {
    const tempData = [];
    const metricsKeys = Object.keys(data.metrics);
    const metaKeys = Object.keys(data.meta);
    tempData.push({
      Category: 'asset_class',
      Value: data.asset_class,
    });
    tempData.push({
      Category: 'asset_id',
      Value: data.asset_id,
    });
    tempData.push({
      Category: 'Metrics',
      Value: '',
    });
    metricsKeys.forEach((iterator) => {
      switch (iterator) {
        case 'surface_area': {
          tempData.push({
            Category: 'Surface Area',
            Value: '',
          });
          const surfaceAreaKeys = Object.keys(data.metrics[iterator]);
          surfaceAreaKeys.forEach((subIterator) => {
            tempData.push({
              Category: subIterator,
              Value: data.metrics[iterator][subIterator],
            });
          });
          break;
        }
        case 'coverage': {
          tempData.push({
            Category: 'Coverage',
            Value: '',
          });
          const coverageKeys = Object.keys(data.metrics[iterator]);
          coverageKeys.forEach((subIterator) => {
            tempData.push({
              Category: subIterator,
              Value: data.metrics[iterator][subIterator].area,
            });
          });
          break;
        }
        default: {
          tempData.push({
            Category: iterator,
            Value: data.metrics[iterator],
          });
          break;
        }
      }
    });
    tempData.Meta = '';
    tempData.push({
      Category: 'Meta',
      Value: '',
    });
    metaKeys.forEach((iterator) => {
      tempData.push({
        Category: iterator,
        Value: data.meta[iterator],
      });
    });
    return tempData;
  },
  async exportTableToCSV(data, fileName, config = { header: false }) {
    const x = Papa.unparse(data, config);
    const blob = new Blob([x], {
      type: 'text/csv;charset=utf-8',
    });
    saveAs(blob, `${fileName}.csv`);
  },
  isNumeric(input) {
    const value = Number(input);
    return !Number.isNaN(value) && input != null && input !== '';
  },
  checkNumeric(input) {
    return {
      isNumeric: this.isNumeric(input),
      value: Number(input),
    };
  },
  getNumeric(input, defaultValue) {
    const { isNumeric, value } = this.checkNumeric(input);
    return isNumeric ? value : defaultValue;
  },
  // rejects promise if not resolved within timeout ms
  promiseWithTimeout: (promise, timeout = 4000, calledBy = '') => {
    let timeoutId;
    const timeoutPromise = new Promise((_, reject) => {
      timeoutId = window.setTimeout(() => {
        reject(new Error(`Request timed out in ${calledBy}`));
      }, timeout);
    });
    return {
      promiseOrTimeout: Promise.race([promise, timeoutPromise]),
      timeoutId,
    };
  },
  colorsBasedOnNumber: (number) => {
    const color = DISTRIBUTED_COLORS[Math.abs(number) % distributedColorsLength];
    return { color, chromaColor: chroma(color).alpha(0.5) };
  },

  stringChecksum: (string) =>
    [...string].reduce((accumulate, current) => {
      // eslint-disable-next-line no-bitwise
      const hash = (accumulate << 5) - accumulate + current.charCodeAt();
      // eslint-disable-next-line no-bitwise
      return hash & hash;
    }, 0),
};

export const flushPromises = () => new Promise(setImmediate);
